/**
 * Structural traversal over the ADR-0006 edge model (`inputs[].sourceSteps[]`).
 *
 * Shared by the backend (draft mutation, publish validation, invite dispatch)
 * and the frontend draft editor so both read the SAME edge set. The rule used
 * to live in five hand-copied places across three packages and they drifted —
 * notably the `previousStep` start fallback, which the publish gate honoured
 * and no client traversal did (fixed twice, 20 minutes apart, on 2026-08-18).
 *
 * Pure structure only: ids are read through {@link GraphIdLike}, never
 * coerced. ObjectId coercion is a Mongoose concern and stays backend-side in
 * `shared/utils/draft/draft-graph-traversal.ts`, which wraps this core.
 */ /** An edge id in either carrier: a hex string (contracts) or an ObjectId (Mongoose). */ /**
 * Iterate every `sourceSteps[]` entry under every `previous_output` input.
 * Used by graph traversals that don't care about event inputs (event-sourced
 * inputs are entry points, not graph edges). Covers steps and assignments —
 * their `inputs[]` shapes are interchangeable here.
 */ export function forEachSourceStep(inputs, cb) {
    for (const input of inputs ?? []){
        if (input?.inputType !== 'previous_output') continue;
        for (const src of input.sourceSteps ?? []){
            if (src) cb(src);
        }
    }
}
/**
 * Does any `sourceSteps[]` entry under a `previous_output` input satisfy
 * `pred`? The short-circuiting predicate form of {@link forEachSourceStep} —
 * edge lookups run once per node per draft assignment, so they must stop at
 * the first match instead of walking every input.
 */ export function someSourceStep(inputs, pred) {
    for (const input of inputs ?? []){
        if (input?.inputType !== 'previous_output') continue;
        for (const src of input.sourceSteps ?? []){
            if (src && pred(src)) return true;
        }
    }
    return false;
}
export function previousOutputInputs(inputs) {
    return (inputs ?? []).filter((input)=>input?.inputType === 'previous_output');
}
/**
 * Iterate every `event` input, yielding the optional `eventId` (present when
 * the input is tied to a first-class event node) and its `sourceSteps[]`
 * (the START-tag mechanism reuses `sourceSteps` on event inputs). Used by
 * connectivity analysis, which treats event nodes and START-tagged event
 * inputs as edges in the undirected graph.
 */ export function forEachEventInput(inputs, cb) {
    for (const input of inputs ?? []){
        if (input?.inputType !== 'event') continue;
        const sources = (input.sourceSteps ?? []).filter((src)=>!!src);
        cb(input.eventId ?? undefined, sources);
    }
}
/**
 * Is the node a process entry point — i.e. attached to one of the draft's
 * start-event rows? A start attachment is an upstream edge in everything but
 * storage shape, so connectivity-style gates (publish validation, invite
 * dispatch, dirty computation) must count it alongside `previous_output`
 * inputs.
 *
 * The canonical form is an `event` input whose `eventId` names a start event.
 * The legacy forms — a start-anchored `previousStep`, and a start-event id
 * tagged into `sourceSteps` — are opt-in via {@link StartAnchorOptions} so
 * each caller declares which of them it accepts instead of re-deriving it.
 */ export function hasStartEventInput(inputs, startEventIds, options = {}) {
    if (startEventIds.size === 0) return false;
    const isStart = (id)=>id != null && startEventIds.has(typeof id === 'string' ? id : id.toString());
    if (isStart(options.previousStep)) return true;
    for (const input of inputs ?? []){
        if (!input) continue;
        if (input.inputType === 'event' && isStart(input.eventId)) return true;
        if (!options.sourceTags) continue;
        for (const src of input.sourceSteps ?? []){
            if (isStart(src?.stepId)) return true;
        }
    }
    return false;
}
/**
 * Rewrite the `sourceSteps[]` of every `previous_output` input — the trimming
 * loop every edge-removal path hand-rolled (disconnect draft/flow, rule
 * pruning, rule-edge reconciliation, merge). `rewrite` returns the source
 * unchanged, a replacement, or `null` to drop that edge.
 *
 * Two invariants the hand-rolled copies each re-derived, and which callers
 * depend on:
 * - A `previous_output` input left with no sources is removed, INCLUDING one
 *   that arrived empty. An empty `sourceSteps[]` is an edge-less carrier that
 *   `backfillSourceStepsFromPreviousStep` will refill from `previousStep`,
 *   re-synthesizing the edge the caller just trimmed. Dropping a pre-existing
 *   empty counts as a change, so a node carrying one flips from ref-equal to
 *   a write once — deliberate data cleanup. EVERY caller inherits it, on
 *   drafts (`removeSourceFromInputs`, `disconnect-draft-steps`,
 *   `prune-stale-rule-references`, `reconcile-rule-edge-bindings`,
 *   `merge-draft-nodes`) and on published `ProcessStepRecording` documents
 *   (`insert-flow-step`), where it is the one path that persists the cleanup
 *   to live rows.
 * - Otherwise nothing changed → the ORIGINAL array is returned, ref-equal,
 *   with the untouched inputs ref-equal too. Callers use that to skip a write.
 *
 * Event inputs pass through untouched: they are entry points, not edges.
 */ export function rewriteSourceSteps(inputs, rewrite) {
    const list = inputs ?? [];
    let changed = false;
    const next = [];
    for (const input of list){
        if (input?.inputType !== 'previous_output') {
            next.push(input);
            continue;
        }
        const sources = input.sourceSteps ?? [];
        let sourcesChanged = false;
        const nextSources = [];
        for (const src of sources){
            const rewritten = rewrite(src);
            if (rewritten === null) {
                sourcesChanged = true;
                continue;
            }
            if (rewritten !== src) sourcesChanged = true;
            nextSources.push(rewritten);
        }
        if (nextSources.length === 0) {
            changed = true;
            continue;
        }
        if (!sourcesChanged) {
            next.push(input);
            continue;
        }
        changed = true;
        next.push({
            ...input,
            sourceSteps: nextSources
        });
    }
    return changed ? next : list;
}

//# sourceMappingURL=draft-graph-traversal.js.map